import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type {
  NewProviderAccount,
  Operator,
  ProviderAccountSummary,
  ProviderEnvironment,
  Reason,
} from "../admin-client";
import {
  useAddProviderAccount,
  useProviderAccounts,
  useProviderTemplates,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can, isPlatformStaff } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { HealthBadge, StatusBadge, when } from "./common";

/**
 * Providers — the upstreams THIS TENANT runs on, and the keys they run with.
 *
 * The screen is the tenant half of M3. The platform's definitions live on
 * Templates, which only platform staff maintain; here an operator picks one of
 * those definitions and supplies their own credential, and from then on their
 * traffic is spent against their own account. Before this split there was one
 * table of accounts and every tenant's prompt went out on the platform's keys.
 *
 * Environment is a column rather than a filter because a prod account and a
 * dev account with the same label are the pair an operator must never confuse,
 * and a filtered view hides exactly that confusion.
 */

const WRITE = "providers:write";

/** 0006's CHECK, mirrored so the picker offers what the API accepts. There is
 *  no default selection: guessing "dev" for an operator who did not choose is
 *  how a production key lands on an account nothing routes to. */
const ENVIRONMENTS: ProviderEnvironment[] = ["dev", "staging", "prod"];

export function Providers() {
  return <RequireOperator>{(session) => <ProviderList operator={session.operator} />}</RequireOperator>;
}

function ProviderList({ operator }: { operator: Operator }) {
  const accounts = useProviderAccounts();
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Platform staff READ across every tenant and cannot add an account: the act
  // needs an owner, and the API refuses them with a 403 rather than picking
  // one (see AddProviderFromTemplate). The button is hidden for the same
  // reason every other control here is — so the rule is met as a sentence
  // instead of as an audited denial.
  const canAdd = !isPlatformStaff(operator) && can(operator, WRITE);

  if (accounts.isPending) {
    return <Spinner label="Loading provider accounts" />;
  }
  if (accounts.error) {
    return <ErrorAlert error={accounts.error} title="Could not load the provider accounts" />;
  }

  const rows = accounts.data?.provider_accounts ?? [];

  return (
    <>
      <PageHeader
        title="Providers"
        description="The upstream accounts your inference runs on, and the state of the keys they run with. Each account holds your own credential; nothing is provisioned for you."
        actions={
          canAdd ? (
            <Button
              variant="primary"
              onClick={() => {
                setNotice(null);
                setAdding((open) => !open);
              }}
            >
              {adding ? "Cancel" : "Add provider"}
            </Button>
          ) : undefined
        }
      />
      {notice ? (
        <Alert tone="success" title="Provider added">
          <p>{notice}</p>
        </Alert>
      ) : null}
      {isPlatformStaff(operator) ? (
        <Alert tone="info" title="You are seeing every tenant's accounts">
          <p>
            Adding a provider files a key under one company, so it is a
            tenant&rsquo;s own act and the platform cannot do it for them. What
            the platform maintains is the list of providers they may choose
            from — <Link to="/provider-templates">Templates</Link>.
          </p>
        </Alert>
      ) : null}
      {adding ? (
        <Card>
          <AddProviderForm
            onClose={() => setAdding(false)}
            onDone={(message) => {
              setNotice(message);
              setAdding(false);
            }}
          />
        </Card>
      ) : null}
      <Card>
        <DataTable<ProviderAccountSummary>
          rows={rows}
          rowKey={(row) => row.id}
          empty={
            canAdd
              ? "You have not added a provider yet. Until you do, there is no key of yours for your traffic to run on."
              : "No provider account has been registered on this deployment yet."
          }
          columns={[
            {
              key: "label",
              header: "Account",
              render: (row) => (
                <Link to="/providers/$accountId" params={{ accountId: row.id }}>
                  {row.label || row.provider}
                </Link>
              ),
            },
            { key: "provider", header: "Provider", render: (row) => <code>{row.provider}</code> },
            {
              key: "environment",
              header: "Environment",
              // prod is called out on sight. Everything an operator does to a
              // prod account lands on paying customers, and reading the word
              // in a grey column is how that gets missed.
              render: (row) => (
                <Badge tone={row.environment === "prod" ? "warning" : "neutral"}>
                  {row.environment}
                </Badge>
              ),
            },
            { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
            { key: "health", header: "Health", render: (row) => <HealthBadge health={row.health} /> },
            {
              key: "credentials",
              header: "Credentials",
              align: "end",
              // "0 / 3" is the row that matters: an active account with no
              // active credential is an outage the status column calls
              // healthy, so the two numbers are shown together rather than as
              // a total. Rotation and revocation are on the account page.
              render: (row) => (
                <Badge tone={row.active_credential_count > 0 ? "neutral" : "danger"}>
                  {row.active_credential_count} / {row.credential_count} active
                </Badge>
              ),
            },
            {
              key: "region",
              header: "Region",
              render: (row) =>
                row.region ? <code>{row.region}</code> : <span className="esm-muted-text">—</span>,
            },
            { key: "created", header: "Registered", render: (row) => when(row.created_at) },
          ]}
        />
      </Card>
    </>
  );
}

interface AddFormState {
  template_id: string;
  environment: ProviderEnvironment | "";
  label: string;
  base_url: string;
  credential_label: string;
  secret: string;
}

/**
 * The add-provider form: one published template, plus one pasted key.
 *
 * The secret lives in this component's state and in the request body, and
 * nowhere else. It is cleared — along with the mutation's cached variables —
 * the instant the account is written, which is the same discipline the
 * rotation form keeps and for the same reason: TanStack holds a mutation's
 * variables after it settles, and a plaintext upstream key is not a thing to
 * leave sitting in a cache.
 */
function AddProviderForm({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  // Unfiltered: the API forces a tenant-scoped read to `published` anyway, so
  // asking for a status here would be this console restating a rule it does
  // not enforce — and getting a different answer from the one the server would
  // give platform staff reading the same route.
  const templates = useProviderTemplates();
  const add = useAddProviderAccount();
  const [form, setForm] = useState<AddFormState>({
    template_id: "",
    environment: "",
    label: "",
    base_url: "",
    credential_label: "",
    secret: "",
  });

  const set = (field: keyof AddFormState) => (value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  const offered = (templates.data?.provider_templates ?? []).filter(
    (template) => template.status === "published",
  );
  const picked = offered.find((template) => template.id === form.template_id);
  const urlError = accountBaseURLError(form.base_url);
  const ready =
    form.template_id !== "" &&
    form.environment !== "" &&
    form.label.trim() !== "" &&
    form.secret.trim() !== "" &&
    urlError === undefined;

  async function run(reason: Reason) {
    const body: NewProviderAccount = {
      template_id: form.template_id,
      environment: form.environment as ProviderEnvironment,
      label: form.label.trim(),
      // Omitted when blank: blank takes the template's endpoint, and sending
      // "" would be asking for an empty override instead of no override.
      base_url: form.base_url.trim() === "" ? undefined : form.base_url.trim(),
      credential_label:
        form.credential_label.trim() === "" ? undefined : form.credential_label.trim(),
      secret: form.secret,
    };
    const result = await add.mutateAsync({ body, reason });
    const sealed = result.credentials[0];
    // The key is out of this component before the success message is drawn.
    setForm((current) => ({ ...current, secret: "" }));
    add.reset();
    onDone(
      sealed
        ? `${result.provider_account.label} is yours, on a key ending ${sealed.last_four}. It is sealed and will not be shown again.`
        : `${result.provider_account.label} is yours.`,
    );
  }

  if (templates.isPending) {
    return <Spinner label="Loading the providers you can add" />;
  }
  if (templates.error) {
    return <ErrorAlert error={templates.error} title="Could not load the provider catalogue" />;
  }

  return (
    <div className="esm-subform">
      <h3>Add a provider</h3>
      <p className="esm-muted-text">
        Pick a provider the platform offers and supply your own key for it.
        The key is sealed on arrival and never shown again — not here, not on
        the account page, not to platform staff. Your requests are then spent
        against this account and no other tenant&rsquo;s.
      </p>
      <ErrorAlert error={add.error} title="The provider was not added" />
      {offered.length === 0 ? (
        <Alert tone="warning" title="Nothing is on offer yet">
          <p>
            The platform has not published a provider you can add. Until it
            does, there is nothing to put a key against.
          </p>
        </Alert>
      ) : null}
      <div className="esm-form-grid">
        <Field
          label="Provider"
          htmlFor="add-provider-template"
          hint={
            picked
              ? `Calls go to ${picked.base_url} unless you override it below.`
              : "What the platform offers today."
          }
        >
          <Select
            id="add-provider-template"
            value={form.template_id}
            onChange={(event) => set("template_id")(event.target.value)}
          >
            <option value="">Choose a provider…</option>
            {offered.map((template) => (
              <option key={template.id} value={template.id}>
                {template.display_name} ({template.provider})
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Environment"
          htmlFor="add-provider-environment"
          hint="Which of your environments this key belongs to. There is no default: a production key on a development account is a key nothing routes to."
        >
          <Select
            id="add-provider-environment"
            value={form.environment}
            onChange={(event) => set("environment")(event.target.value)}
          >
            <option value="">Choose an environment…</option>
            {ENVIRONMENTS.map((environment) => (
              <option key={environment} value={environment}>
                {environment}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Account label"
          htmlFor="add-provider-label"
          hint="What you call this account. It is half of what lets you hold a second account with the same provider."
        >
          <Input
            id="add-provider-label"
            value={form.label}
            onChange={(event) => set("label")(event.target.value)}
          />
        </Field>
        <Field
          label="Endpoint override"
          htmlFor="add-provider-base-url"
          hint="Optional. Leave blank to call the provider directly; set it to send this account's traffic through a gateway of your own."
          error={urlError}
        >
          <Input
            id="add-provider-base-url"
            value={form.base_url}
            invalid={urlError !== undefined}
            placeholder={picked?.base_url ?? "https://…"}
            onChange={(event) => set("base_url")(event.target.value)}
          />
        </Field>
        <Field
          label="Key label"
          htmlFor="add-provider-credential-label"
          hint="Optional. What this key is called in the pool when you come to rotate it."
        >
          <Input
            id="add-provider-credential-label"
            value={form.credential_label}
            onChange={(event) => set("credential_label")(event.target.value)}
          />
        </Field>
      </div>
      <Field
        label="Your key"
        htmlFor="add-provider-secret"
        hint="Pasted once. The platform stores it encrypted, shows you its last four, and has no route that can read it back."
      >
        <Input
          id="add-provider-secret"
          type="password"
          autoComplete="off"
          value={form.secret}
          onChange={(event) => set("secret")(event.target.value)}
        />
      </Field>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={add.isPending}
          disabled={!ready}
          mutation={{
            title: `Add ${picked?.display_name ?? "this provider"}`,
            tone: "primary",
            description: (
              <p>
                The account is filed under your company and the key is sealed
                into its pool. Your traffic can only ever be spent against your
                own key — and until a routing profile of yours names this
                account, nothing is routed to it at all.
              </p>
            ),
            confirmLabel: "Add provider",
            run,
          }}
        >
          Add provider
        </ReasonButton>
        <Button variant="ghost" disabled={add.isPending} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * validateAccountBaseURL's rule, mirrored.
 *
 * http IS accepted here and refused on a template, which is the whole reason
 * both columns exist: the template is the vendor's public https endpoint, and
 * this override is a private one — a tenant's own gateway, or the local mock,
 * which speaks plaintext.
 */
function accountBaseURLError(raw: string): string | undefined {
  const text = raw.trim();
  if (text === "") {
    return undefined;
  }
  let parsed: URL;
  try {
    parsed = new URL(text);
  } catch {
    return "Not a URL. It should look like https://gateway.example.com.";
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return "An http or https origin.";
  }
  if (parsed.pathname !== "/" && parsed.pathname !== "") {
    return "An origin, with no path — each adapter composes its own and would otherwise send it twice.";
  }
  if (parsed.search !== "" || parsed.hash !== "") {
    return "An origin, with no query or fragment.";
  }
  return undefined;
}
